import styled from "@emotion/styled"
import Breadcrumb from "components/breadcrumb/Breadcrumb"
import Category from "components/footer/Category"
import ControlPanel from "components/footer/ControlPanel"
import { Crown } from "components/footer/Icons"
import Legal from "components/footer/Legal"
import Underfooter from "components/footer/Underfooter"
import { useNav } from "components/nav-v2/Wrapper"
import Push from "components/push/PushOrg"
import Share from "components/share/Share"
import { useConsole } from "contexts/Console"
import { Provider } from "contexts/Footer"
import { useNavigation } from "contexts/Navigation"
import getMediaQuery from "css/breakpoints"
import { fullGrid } from "css/grid"
import { animate } from "framer-motion"
import { Fragment, Suspense, useLayoutEffect, useRef } from "react"
import { useSeparator } from "react-aria"
import { Route, StaticRouter, Switch } from "react-router-dom"

const Root = styled.footer`
  width: 100vw;
`

const Wrapper = styled.div`
  background: rgb(var(--light-grey));
  position: relative;
  z-index: 1;
`

const Main = styled.div`
  ${fullGrid}
  padding-block: 2.5rem 1.5rem;

  ${getMediaQuery("m")} {
    padding-block: 3.75rem 2.75rem;
  }
`

const Brand = styled.section`
  display: flex;
  justify-content: center;
  padding-block: 2.5rem 3.75rem;

  ${getMediaQuery("m")} {
    padding-block-end: 2.5rem;

    svg {
      height: 77px;
    }
  }
`

const Navigation = styled.nav`
  display: contents;
`

const Section = styled.ul`
  column-count: 2;
  column-gap: var(--grid-gap);
  grid-column: main;
  --baseline-font-size: clamp(0.875rem, 0.8125rem + 0.1563vw, 1rem);
  --baseline: calc(1.79 * var(--baseline-font-size));
  /* uncomment below to debug baseline-grid */
  /* background-image: repeating-linear-gradient(
    to bottom,
    transparent 0px,
    transparent calc(var(--baseline) - 1px),
    red calc(var(--baseline) - 1px),
    red var(--baseline)
  ); */

  ${getMediaQuery("m")} {
    column-count: 3;
  }

  ${getMediaQuery("xl")} {
    grid-column: col 3 / span 9;
  }

  > li {
    break-inside: avoid;
    display: block;
    line-height: var(--baseline);
    margin-block-end: var(--baseline);
    padding-inline-end: calc(var(--grid-gap));

    // Safari
    box-sizing: border-box;
    width: 100%;

    ${getMediaQuery("l")} {
      width: calc(100% - var(--grid-col-unit));
    }

    .reverseIcon {
      svg {
        height: 0.75rem;
        width: 0.75rem;
      }
    }

    // force break before 2th and 7th item in 3 column layout
    ${getMediaQuery("m")} {
      &:nth-of-type(2),
      &:nth-of-type(7) {
        break-before: column;
      }
    }
  }
`

function Separator(props) {
  const { separatorProps } = useSeparator(props)

  return (
    <div
      {...separatorProps}
      style={{
        background: "rgb(var(--grey))",
        height: "1px",
        marginInline: "var(--outer-margin)",
      }}
    />
  )
}

export default function Footer(props) {
  const {
    categories,
    social,
    share,
    languages,
    breadcrumb = [],
    slugForBreadcrumb,
    accessibility,
    wechat,
    firstlinkicp,
    secondlinkicp,
    listPush,
    legal_links_p13n,
    categories_aria_label = "Footer navigation",
  } = props
  const console = useConsole()
  const navigation = useNavigation()
  const rroot = useRef(null)
  const nav = useNav()
  const rwrapper = useRef(null)

  useLayoutEffect(function forceSubnavUnpin() {
    const observer = new IntersectionObserver(([entry]) => {
      animate(nav.sub.toggle.motion.forceUnpinned, +entry.isIntersecting)
    })

    observer.observe(rwrapper.current)

    return () => {
      observer.unobserve(rwrapper.current)
      observer.disconnect()
    }
  })

  console.verbose("Footer(%o)", props)
  return (
    <Provider value={{}}>
      <Root role='contentinfo' ref={rroot} id='footer'>
        <Brand>
          <Crown />
        </Brand>
        <Breadcrumb items={breadcrumb} slug={slugForBreadcrumb} />
        <Share {...share} />
        <Wrapper ref={rwrapper} className='sections'>
          <Main>
            <Navigation aria-label={categories_aria_label}>
              <Section>
                {categories.map((item, index) => (
                  // Insert accessibility in the second to last position and social right before the last category.
                  <Fragment key={item.label}>
                    {index === categories.length - 2 && !accessibility?.hide && <Category accessibility {...accessibility} />}
                    {index === categories.length - 1 && <Category social {...social} wechat={wechat} />}
                    <Category {...item} />
                  </Fragment>
                ))}
              </Section>
            </Navigation>
          </Main>
          <Separator />
          <ControlPanel {...languages} firstlinkicp={firstlinkicp} secondlinkicp={secondlinkicp} />
          <Legal legal_links={legal_links_p13n} />
        </Wrapper>
        <Underfooter>
          <StaticRouter location={navigation.slug}>
            <Switch>
              {listPush.map(item => {
                return (
                  <Route key={item._metadata?.uid} path={item.url} exact={false}>
                    <Suspense>
                      <Push {...item.reference[0]} />
                    </Suspense>
                  </Route>
                )
              })}
            </Switch>
          </StaticRouter>
        </Underfooter>
      </Root>
    </Provider>
  )
}
